import type { ExecutionModeV1, SystemAiPolicyResponseV1 } from "@adpilot/shared";
import { IconClock, IconList, IconShieldCheck } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { CoverageMetric, ReviewMetric } from "./ai-policy-cards";

export function ReviewStep({
  displayShopName,
  goalLabel,
  targetAcos,
  modeLabel,
  mode,
  eligibleCampaigns,
  excludedCampaigns,
  nextRunTime,
  managedGroupResumeImpact,
}: {
  displayShopName: string;
  goalLabel: string;
  targetAcos: string;
  modeLabel: string;
  mode: ExecutionModeV1;
  eligibleCampaigns: number;
  excludedCampaigns: number;
  nextRunTime: string;
  managedGroupResumeImpact: SystemAiPolicyResponseV1["managedGroupResumeImpact"] | undefined;
}) {
  const { t } = useTranslation("dashboard");
  return (
    <div className="flex h-full flex-col gap-3 px-[22px] py-[28px]">
      <div className="flex items-center justify-between">
        <h3 className="text-[13px] font-semibold text-ink">{t("policyDialog.review.title")}</h3>
        <span className="text-[10px] text-muted-foreground">{displayShopName}</span>
      </div>
      <div className="grid h-[82px] shrink-0 grid-cols-[1.7fr_0.78fr_0.9fr] divide-x divide-line overflow-hidden rounded-[10px] border border-line">
        <ReviewMetric label={t("policyDialog.businessGoal")} value={goalLabel} />
        <ReviewMetric label={t("policyDialog.targetAcos")} value={`${targetAcos}%`} large />
        <ReviewMetric
          label={t("policyDialog.executionMode")}
          value={modeLabel}
          dot={mode === "semi-auto"}
        />
      </div>
      <div className="flex h-20 shrink-0 items-center rounded-[10px] border border-line px-4">
        <span className="flex size-9 items-center justify-center rounded-[8px] bg-subtle">
          <IconList className="size-4" />
        </span>
        <div className="ml-3 min-w-0 flex-1">
          <p className="text-[11px] font-semibold text-ink">{t("policyDialog.coverage.title")}</p>
          <p className="mt-0.5 text-[9px] text-muted-foreground">
            {t("policyDialog.coverage.description")}
          </p>
        </div>
        <CoverageMetric value={eligibleCampaigns} label={t("policyDialog.coverage.eligible")} />
        <span className="mx-4 h-8 w-px bg-line" />
        <CoverageMetric
          value={excludedCampaigns}
          label={t("policyDialog.coverage.excluded")}
          muted
        />
      </div>
      <div className="flex h-[62px] shrink-0 items-center rounded-[10px] border border-line px-4">
        <span className="flex size-9 items-center justify-center rounded-[8px] bg-subtle">
          <IconClock className="size-4" />
        </span>
        <div className="ml-3 flex-1">
          <p className="text-[11px] font-semibold text-ink">{t("policyDialog.schedule.title")}</p>
          <p className="text-[9px] text-muted-foreground">
            {t("policyDialog.schedule.description")}
          </p>
        </div>
        <div className="text-right">
          <p className="text-[11px] font-semibold text-ink">
            {t("policyDialog.schedule.time", { time: nextRunTime })}
          </p>
          <p className="text-[9px] text-muted-foreground">
            {t("policyDialog.schedule.nextRunToday")}
          </p>
        </div>
      </div>
      <div className="flex h-[52px] shrink-0 items-center gap-3 rounded-[9px] border border-green/15 bg-green/5 px-4">
        <IconShieldCheck className="size-4 text-green-11" stroke={1.8} />
        <div>
          <p className="text-[10px] font-semibold text-ink">
            {t("policyDialog.guardrailsActive.title")}
          </p>
          <p className="text-[9px] text-muted-foreground">
            {t("policyDialog.guardrailsActive.description")}
          </p>
        </div>
      </div>
      {(managedGroupResumeImpact?.groupCount ?? 0) > 0 ? (
        <p className="text-center text-[10px] text-muted-foreground">
          {t("policyDialog.review.managedGroupResumeImpact", managedGroupResumeImpact)}
        </p>
      ) : null}
    </div>
  );
}
